import { Link, useNavigate } from "react-router-dom";
import "./style.css";
import Screen from "../Layouts/Screen/Screen";
import { Helmet } from "react-helmet-async";
import { useEffect, useRef, useState } from "react";
import { useMessage, useTheme } from "../../global-components/src";
import { LoadingThreeCircle } from "../../global-components/src/components/spinner/spiner";
import axios from "axios";
import { useAuth } from "../../global-components/src";
import { ChevronRight } from "react-feather";
import { Ads } from "../../types";
import { errorMessage } from "../../utils/TextManagerTwo";
import ConfirmationModal from "../../components/Modals/ConfirmationModal";
import { CirclePlus, Megaphone, MegaphoneOff, Trash2 } from "lucide-react";
import { PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { Icon } from "../../global-components/src/components/icon";
import ReactMarkdown from "react-markdown";

function getAdsBadge(status: string): { cls: string; label: string } {
    if (status === "ativo")               return { cls: "ads-badge-active",     label: "✔ Ativo" };
    if (status === "online")              return { cls: "ads-badge-online",     label: "📢 Online" };
    if (status === "validando_conteudo")  return { cls: "ads-badge-validating", label: "🔎 Validando" };
    if (status === "pendente")            return { cls: "ads-badge-pending",    label: "💰 Pagamento pendente" };
    if (status === "off")                 return { cls: "ads-badge-off",        label: "⛔ Atingiu o esperado" };
    if (status.includes("Reprovado"))     return { cls: "ads-badge-rejected",   label: "❌ Reprovado" };
    return { cls: "ads-badge-off", label: status };
}

const MyAds = () => {
    const endOfListRef = useRef<HTMLDivElement | null>(null);
    const [ads, setAds] = useState<Ads[] | null>(null);
    const [adsIdToDelete, setAdsIdToDelete] = useState<number | null>(null);
    const { setMessage } = useMessage();
    const [loading, setLoading] = useState(false);
    const [loadingAnalyze, setLoadingAnalyze] = useState(false);
    const [end, setEnd] = useState(false);
    const [page, setPage] = useState(1);
    const { token, user } = useAuth();
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();
    const [password, setPassword] = useState("");
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [errorModal, setErrorModalPassword] = useState("");
    const closeRef = useRef<HTMLButtonElement | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [analyze, setAnalyze] = useState("");

    const loadData = async (newPage?: number) => {
        const currentPage = newPage ?? page + 1;
        if (loading || end) return;
        setLoading(true);
        try {
            const response = await axios.get(PACOCA_API_URL + `/ads?page=${currentPage}`, {
                headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${token}` }
            });
            const data = response.data;
            if (data?.ads?.length > 0) {
                setAds(prev => [...(prev || []), ...data.ads]);
            } else {
                setEnd(true);
            }
        } catch (err: any) {
            setMessage(errorMessage(err), "error");
            setEnd(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadAnalyze = async () => {
            const cached = localStorage.getItem(`analyzeAds${user?.id}`);
            if (cached?.length) { setAnalyze(cached); return; }
            setLoadingAnalyze(true);
            try {
                const response = await fetch(`${PACOCA_API_URL}/ai/analyze`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                        Accept: "text/event-stream",
                        "Cache-Control": "no-cache",
                        Connection: "keep-alive"
                    },
                    body: JSON.stringify({ type: "ads" })
                });
                if (!response.ok || !response.body) {
                    const errorJson = await response.json();
                    throw new Error(errorJson.error || `Erro ${response.status}`);
                }
                const reader = response.body.getReader();
                const decoder = new TextDecoder("utf-8");
                let finalText = "", buffer = "";
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split("\n");
                    buffer = lines.pop() || "";
                    for (const lineRaw of lines) {
                        const line = lineRaw.trim();
                        if (!line.startsWith("data:")) continue;
                        const data = line.replace("data:", "").trim();
                        if (data === "[DONE]") break;
                        try {
                            const json = JSON.parse(data);
                            const content = json.choices?.[0]?.delta?.content;
                            if (!content) continue;
                            finalText += content;
                            localStorage.setItem(`analyzeAds${user?.id}`, finalText);
                            setAnalyze(finalText);
                        } catch { /* ignore */ }
                    }
                }
            } catch (err: any) {
                setAnalyze(errorMessage(err));
            } finally {
                setLoadingAnalyze(false);
            }
        };
        loadAnalyze();
    }, []);

    useEffect(() => {
        const modalBody = document.getElementById("modal-comments-body");
        const handleScroll = () => {
            if (!modalBody) return;
            const { scrollTop, clientHeight, scrollHeight } = modalBody;
            if (scrollTop + clientHeight >= scrollHeight - 50 && !end && !loading)
                setPage(p => p + 1);
        };
        if (modalBody) modalBody.addEventListener("scroll", handleScroll);
        return () => { if (modalBody) modalBody.removeEventListener("scroll", handleScroll); };
    }, [end, loading]);

    useEffect(() => { loadData(page); }, [page]);

    const confirmDeleteAds = async (password: string) => {
        setIsLoading(true);
        try {
            if (!password) { setErrorModalPassword("Informe sua senha"); setIsLoading(false); return; }
            const response = await axios.delete(`${PACOCA_API_URL}/ads/${adsIdToDelete}`, {
                data: { password },
                headers: { Accept: "application/json", Authorization: `Bearer ${token}` }
            });
            setShowConfirmation(false);
            setMessage(response.data.message, "success");
            setAds(prev => prev ? prev.filter(s => s.id !== adsIdToDelete) : []);
        } catch (err: any) {
            setErrorModalPassword(errorMessage(err));
        } finally {
            setPassword("");
            setIsLoading(false);
        }
    };

    const onClose = () => { setPassword(""); setShowConfirmation(false); };

    return (
        <>
            <ConfirmationModal
                show={showConfirmation}
                onClose={onClose}
                onConfirm={() => confirmDeleteAds(password)}
                title="Remover Anúncio?"
                text="Se o anúncio estiver online, ele será removido imediatamente e não é possível reverter essa ação"
                requirePassword={true}
                setPassword={setPassword}
                errorMessage={errorModal}
                isLoading={isLoading}
                closeRef={closeRef}
            />

            <Screen paddingTopTitle title="Meus Anúncios" contentMenu={
                <span className="cursor-pointer" onClick={() => navigate("/anuncios/novo")} style={{ marginTop: 2 }}>
                    <Icon size={24} style={{ margin: 0, cursor: "pointer" }} invertColor={!isDarkMode} name="add_circle">
                        <CirclePlus opacity={0.6} size={23} />
                    </Icon>
                </span>
            }>
                <Helmet>
                    <title>Paçoca - Meus Anúncios</title>
                    <meta name="description" content="Paçoca - Veja seus anúncios no Paçoca." />
                    <meta name="keywords" content="Paçoca, rede social brasileira, anúncios, campanha" />
                    <meta name="author" content="Paçoca Inc." />
                    <meta property="og:title" content="Paçoca - Meus Anúncios" />
                    <meta property="og:description" content="Paçoca - Veja seus anúncios no Paçoca." />
                    <meta property="og:image" content="/img/link-preview.png?v=1" />
                    <meta property="og:url" content={window.location.href} />
                    <meta property="og:type" content="website" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content="Paçoca - Meus Anúncios" />
                    <meta name="twitter:description" content="Paçoca - Veja seus anúncios no Paçoca." />
                    <meta name="twitter:image" content="/img/link-preview.png?v=1" />
                </Helmet>

                <div className="ads-list">
                    {ads?.map((ad, index) => {
                        const badge = getAdsBadge(ad.status);
                        return (
                            <div key={ad.id ?? index} className="ads-item">
                                <div className="ads-item-icon-wrap">
                                    <Megaphone size={16} />
                                </div>

                                <Link
                                    to={`/anuncios/meus-anuncios/${ad.id}`}
                                    className="ads-item-body"
                                    style={{ textDecoration: "none" }}
                                >
                                    <span className="ads-item-name">{ad.name}</span>
                                    <span className={`ads-badge ${badge.cls}`}>{badge.label}</span>
                                </Link>

                                <button
                                    type="button"
                                    className="ads-delete-btn"
                                    title="Remover anúncio"
                                    onClick={e => {
                                        e.stopPropagation();
                                        setAdsIdToDelete(ad.id);
                                        setShowConfirmation(true);
                                    }}
                                >
                                    <Trash2 size={14} />
                                </button>

                                <Link to={`/anuncios/meus-anuncios/${ad.id}`}
                                    style={{ color: "inherit", opacity: 0.35, display: "flex", alignItems: "center" }}>
                                    <ChevronRight size={16} />
                                </Link>
                            </div>
                        );
                    })}

                    <div ref={endOfListRef} />

                    {loading && (
                        <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
                            <LoadingThreeCircle />
                        </div>
                    )}

                    {!loading && !ads?.length && (
                        <div className="ads-empty">
                            <MegaphoneOff size={38} className="ads-empty-icon" />
                            <span style={{ fontSize: 14, fontWeight: 500 }}>Nenhum anúncio publicado</span>
                            <span style={{ fontSize: 13 }}>
                                <Link to="/anuncios/novo">Crie seu primeiro anúncio</Link>
                            </span>
                        </div>
                    )}

                    {(analyze.length > 0 || loadingAnalyze) && (
                        <div className="ads-analyze-card">
                            <p className="ads-analyze-title">✨ Análise do Paçoca AI</p>
                            <div className="ads-analyze-body">
                                {!analyze.length && loadingAnalyze ? (
                                    <span style={{ opacity: 0.5, fontSize: 12 }}>Carregando análise…</span>
                                ) : (
                                    <ReactMarkdown>{analyze}</ReactMarkdown>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </Screen>
        </>
    );
};

export default MyAds;
